// Agents overview — grid of cards with avatar, status, schedule.
// Click a card → /agents/:id detail.

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { agentsApi, type AgentSummary } from "../lib/api";
import { AgentAvatar } from "../components/AgentAvatar";

export function AgentsPage() {
  const q = useQuery({
    queryKey: ["agents"],
    queryFn: agentsApi.list,
    refetchInterval: 30_000, // every 30s — agents can change state quickly
  });
  const pendingQ = useQuery({
    queryKey: ["agents-pending-count"],
    queryFn: agentsApi.pending,
    refetchInterval: 30_000,
    staleTime: 20_000,
  });
  const pendingCount = pendingQ.data?.pending.length ?? 0;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">Agents</h1>
          <p className="text-ink-600 text-sm mt-1">
            Autonomous task runners. Each one owns a slice of operator work; click for actions log + controls.
            See <code className="bg-ink-100 px-1 rounded text-xs">docs/agents-design.md</code> for the design.
          </p>
        </div>
        <Link
          to="/agents/pending"
          className={
            "inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors " +
            (pendingCount > 0
              ? "bg-amber-100 text-amber-900 hover:bg-amber-200"
              : "bg-ink-100 text-ink-700 hover:bg-ink-200")
          }
        >
          Pending approvals
          {pendingCount > 0 && (
            <span className="bg-amber-500 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full">
              {pendingCount}
            </span>
          )}
        </Link>
      </header>

      {q.isLoading && <div className="text-sm text-ink-500">Loading…</div>}
      {q.error && <div className="text-sm text-red-700">Failed: {String((q.error as Error).message)}</div>}
      {q.data && q.data.agents.length === 0 && (
        <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">
          No agents registered yet. Run <code className="bg-ink-100 px-1 rounded text-xs">rankforge agents list</code> to auto-register code-defined agents.
        </div>
      )}
      {q.data && q.data.agents.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {q.data.agents.map((a) => (
            <AgentCard key={a.id} agent={a} />
          ))}
        </div>
      )}
    </div>
  );
}

function AgentCard({ agent }: { agent: AgentSummary }) {
  const status = agentStatus(agent);
  const lastRun = agent.lastRunAt ? relTime(agent.lastRunAt) : "never";
  const nextRun = agent.nextRunAt && agent.enabled ? relTime(agent.nextRunAt, true) : "—";
  return (
    <Link
      to={`/agents/${agent.id}`}
      className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm hover:shadow-md transition no-underline text-ink-900"
    >
      <div className="flex items-start gap-3 mb-3">
        <AgentAvatar id={agent.id} displayName={agent.displayName} emoji={agent.emoji} url={agent.avatarUrl} size={48} />
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-base leading-tight">{agent.displayName}</h3>
          <p className="text-xs text-ink-500 mt-0.5">v{agent.version} · {agent.id}</p>
        </div>
        <StatusBadge status={status} />
      </div>
      <p className="text-sm text-ink-600 mb-3 line-clamp-2">{agent.role}</p>
      <dl className="text-xs space-y-1">
        <Row label="Trust">
          <span className={`px-1.5 py-0.5 rounded text-xs ${trustToneClass(agent.trustPhase)}`}>
            {agent.trustPhase}
          </span>
        </Row>
        <Row label="Last run">{lastRun}</Row>
        <Row label="Next run">{nextRun}</Row>
        <Row label="24h">
          <span>
            <strong className="tabular-nums">{agent.stats.actionsLast24h}</strong>
            <span className="text-ink-400"> / {agent.dailyBudget}</span>
            {agent.stats.failed24h > 0 && (
              <span className="text-red-700 ml-2">{agent.stats.failed24h} failed</span>
            )}
          </span>
        </Row>
        <Row label="30d cost">
          <span className="tabular-nums">
            {agent.stats.spendCents30d > 0
              ? `$${(agent.stats.spendCents30d / 100).toFixed(2)}`
              : <span className="text-ink-400">—</span>}
          </span>
        </Row>
      </dl>
    </Link>
  );
}

function StatusBadge({ status }: { status: ReturnType<typeof agentStatus> }) {
  const styles: Record<typeof status.kind, string> = {
    working: "bg-green-100 text-green-800",
    idle: "bg-amber-100 text-amber-800",
    disabled: "bg-ink-100 text-ink-500",
    dry_run: "bg-blue-100 text-blue-800",
  };
  const dot: Record<typeof status.kind, string> = {
    working: "bg-green-600 animate-pulse",
    idle: "bg-amber-500",
    disabled: "bg-ink-400",
    dry_run: "bg-blue-500",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${styles[status.kind]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot[status.kind]}`} />
      {status.label}
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-ink-500">{label}</dt>
      <dd className="text-ink-700 text-right">{children}</dd>
    </div>
  );
}

export function agentStatus(agent: AgentSummary): { kind: "working" | "idle" | "disabled" | "dry_run"; label: string } {
  if (!agent.enabled) return { kind: "disabled", label: "disabled" };
  if (agent.trustPhase === "dry_run") return { kind: "dry_run", label: "dry-run" };
  // Working = last run within 60s (stale detection).
  if (agent.lastRunAt) {
    const age = Date.now() - new Date(agent.lastRunAt).getTime();
    if (age < 60_000) return { kind: "working", label: "working" };
  }
  return { kind: "idle", label: "idle" };
}

function trustToneClass(phase: string): string {
  return phase === "autonomous"
    ? "bg-green-50 text-green-800"
    : phase === "confirm"
      ? "bg-amber-50 text-amber-800"
      : "bg-blue-50 text-blue-800";
}

function relTime(iso: string, future = false): string {
  const ms = future
    ? new Date(iso).getTime() - Date.now()
    : Date.now() - new Date(iso).getTime();
  // For "future" times (next_run_at) that have already passed, the
  // agent is overdue — the next quickping will pick it up. Show
  // "due now" instead of "in -1m" which reads as a bug.
  if (future && ms <= 0) return "due now";
  const sec = Math.round(ms / 1000);
  if (sec < 60) return future ? `in ${sec}s` : `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return future ? `in ${min}m` : `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return future ? `in ${hr}h` : `${hr}h ago`;
  const d = Math.round(hr / 24);
  return future ? `in ${d}d` : `${d}d ago`;
}
